import { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChartLine, faSpinner } from '@/fontawesome'
import FearGreedIndexDetail from '@/components/market-analysis/FearGreedIndexDetail'
import TrafficLightDetail from '@/components/market-analysis/TrafficLightDetail'
import StockHeatmap from '@/components/market-analysis/StockHeatmap'
import EconomicCalendar from '@/components/market-analysis/EconomicCalendar'
import EarningsCalendar from '@/components/market-analysis/EarningsCalendar'
import { APIRoutes } from '@/services/api.routes'
import type { StocksStatusLight } from '@/models'
import { showErrorToast, handleAPIError } from '@/utils/toasts'

export default function MarketAnalysis() {
  const [stockStatusLight, setStockStatusLight] = useState<StocksStatusLight | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const statusLight = await APIRoutes.getLatestStocksStatusLight()
        setStockStatusLight(statusLight)
      } catch (error) {
        console.error('Error fetching stocks status light:', error)
        handleAPIError(error, 'Fehler beim Laden der Börsenampel')
        showErrorToast('Börsenampel konnte nicht geladen werden')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} className="w-12 h-12 text-blue-600 dark:text-blue-400 animate-spin mb-4" />
            <p className="text-lg text-gray-500 dark:text-gray-400">Lade Marktanalyse...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="py-8">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
            <FontAwesomeIcon icon={faChartLine} className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Marktanalyse
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Umfassende Übersicht über Marktindikatoren, Kalender und Insights
            </p>
          </div>
        </div>
      </div>

      {/* Main Content - Grid Layout */}
      <div className="space-y-8">
        {/* Market Indicators Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TrafficLightDetail stockStatusLight={stockStatusLight} />
          <FearGreedIndexDetail />
        </div>

        {/* Stock Heatmap */}
        <StockHeatmap />

        {/* Economic Calendar */}
        <EconomicCalendar />

        {/* Earnings Calendar */}
        <EarningsCalendar />
      </div>
    </div>
  )
}
